import { Audio } from '@microsoft/microsoft-graph-types';
import { fileFormat } from '../models/fileExtensions';
import Track from '../models/track/track';
import { AudioProperties } from './audioProperties';
import { invalidateTrackSearch } from './trackSearch';

/**
 * The writers for `format`, `bitrate` and `vbr` on a library record - the
 * fields that describe the file rather than the music (see Track).
 *
 * Each returns whether it changed anything, so a refetch that learned
 * nothing new can skip the library write, as applyTagsToTrack does. They
 * only ever overwrite with a value: a drive that names no file, a header
 * that gives no bit rate, leave what the record has.
 *
 * Plain functions over plain data, like every other writer, because tracks
 * are JSON at runtime (ARCHITECTURE.md section 2).
 */

/** Sets `format` from a file name, when the name has an extension. */
export function applyFileFormat(track: Track, fileName: string | null | undefined) {
  const format = fileFormat(fileName);

  if (!format || format === track.format) return false;

  track.format = format;
  invalidateTrackSearch(track);

  return true;
}

function setBitrate(track: Track, bitrate: number, vbr: boolean) {
  const changed = track.bitrate !== bitrate || (track.vbr === true) !== vbr;

  if (!changed) return false;

  track.bitrate = bitrate;

  // Absent rather than false, so a constant rate costs nothing in the file.
  if (vbr) {
    track.vbr = true;
  } else {
    delete track.vbr;
  }

  invalidateTrackSearch(track);

  return true;
}

/** Sets the bit rate from what readAudioProperties found in the file. */
export function applyAudioProperties(track: Track, properties: AudioProperties | undefined) {
  if (!properties || !(properties.bitrate! > 0)) return false;

  return setBitrate(track, properties.bitrate!, properties.vbr === true);
}

/** Sets the bit rate from OneDrive's `audio` facet, which is in kbps already. */
export function applyOneDriveAudioFacet(track: Track, audio: Audio | null | undefined) {
  if (!audio || !(audio.bitrate! > 0)) return false;

  return setBitrate(track, Math.round(audio.bitrate!), audio.isVariableBitrate === true);
}
